/**
 * AddonsList Component (Orchestrator)
 *
 * Orchestrates the rendering of plans in either grid or table view.
 * Handles loading states, error states, pagination, and lazy loads view components.
 */

import type { PlanResponse } from '@monorepo/shared/schemas/plans'
import { Loader2 } from 'lucide-react'
import { lazy, Suspense } from 'react'
import { Button } from '@/shared/components/ui/button'
import { useAddonActions } from '../hooks/useAddonActions'
import type { PlanFilters } from '../types/addon.types'

// Lazy load view components for code splitting
const AddonsGrid = lazy(() =>
  import('./AddonsGrid').then(m => ({ default: m.AddonsGrid }))
)
const AddonsTable = lazy(() =>
  import('./AddonsTable').then(m => ({ default: m.AddonsTable }))
)

interface AddonsListProps {
  plans: PlanResponse[]
  isLoading?: boolean
  error?: Error | null
  viewMode: 'grid' | 'table'
  filters: PlanFilters
  onSort?: (sortBy: string, sortOrder: 'asc' | 'desc') => void
  onPageChange?: (page: number) => void
  onPlanClick?: (plan: PlanResponse) => void
  onPlanUpdate?: (updatedPlan: PlanResponse) => void
  meta?: {
    page: number
    limit: number
    total: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
  }
}

export function AddonsList({
  plans,
  isLoading,
  error,
  viewMode,
  filters: _filters,
  onSort: _onSort,
  onPageChange,
  onPlanClick,
  onPlanUpdate: _onPlanUpdate,
  meta,
}: AddonsListProps) {
  const actions = useAddonActions()

  // Loading state
  if (isLoading) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='flex flex-col items-center gap-4'>
          <Loader2 className='w-8 h-8 animate-spin text-primary' />
          <p className='text-sm text-muted-foreground'>Loading addons...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='text-center'>
          <p className='text-sm text-destructive mb-2'>Failed to load addons</p>
          <p className='text-xs text-muted-foreground'>{error.message}</p>
        </div>
      </div>
    )
  }

  // Empty state
  if (plans.length === 0) {
    return (
      <div className='flex items-center justify-center min-h-[400px]'>
        <div className='text-center'>
          <p className='text-sm text-muted-foreground mb-2'>No addons found</p>
          <p className='text-xs text-muted-foreground'>
            Try adjusting your filters
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      {/* View Content with Suspense for lazy loading */}
      <Suspense
        fallback={
          <div className='flex items-center justify-center min-h-[400px]'>
            <Loader2 className='w-8 h-8 animate-spin text-primary' />
          </div>
        }
      >
        {viewMode === 'grid' ? (
          <AddonsGrid
            plans={plans as never}
            actions={actions}
            onPlanClick={onPlanClick}
          />
        ) : (
          <AddonsTable
            plans={plans as never}
            actions={actions}
            onPlanClick={onPlanClick}
          />
        )}
      </Suspense>

      {/* Pagination */}
      {meta && meta.total_pages > 1 && (
        <div className='flex items-center justify-between mt-6'>
          <div className='text-sm text-muted-foreground'>
            Page {meta.page} of {meta.total_pages}
          </div>
          <div className='flex items-center gap-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={() => onPageChange?.(meta.page - 1)}
              disabled={meta.page <= 1}
            >
              Previous
            </Button>
            <Button
              variant='outline'
              size='sm'
              onClick={() => onPageChange?.(meta.page + 1)}
              disabled={meta.page >= meta.total_pages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
